import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Form, Button, Card, Spinner, Alert } from 'react-bootstrap';
import api from '../api/axios';

const TutorSetup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const group = location.state?.group || '';

  const [numQuestionsInput, setNumQuestionsInput] = useState('');
  const [questionSource, setQuestionSource] = useState('all');
  const [error, setError] = useState('');
  const [counts, setCounts] = useState({ all: 0, new: 0, mixed: 0 });
  const [loadingCounts, setLoadingCounts] = useState(true);
  const [fetchError, setFetchError] = useState('');

  useEffect(() => {
    const fetchCounts = async () => {
      setLoadingCounts(true);
      setFetchError('');
      try {
        const url = group ? `/question-counts.php?group=${group}` : '/question-counts.php';
        const res = await api.get(url);
        setCounts(res.data);
      } catch (err) {
        console.error('Error fetching counts:', err);
        if (err.response) {
          setFetchError(`Server error: ${err.response.status} - ${JSON.stringify(err.response.data)}`);
        } else if (err.request) {
          setFetchError('No response from server. Check if backend is running.');
        } else {
          setFetchError('Error: ' + err.message);
        }
      } finally {
        setLoadingCounts(false);
      }
    };
    fetchCounts();
  }, [group]);

  const validateAndGetNumber = () => {
    if (numQuestionsInput.trim() === '') {
      setError('Number of questions is required.');
      return null;
    }

    const num = parseInt(numQuestionsInput, 10);
    if (isNaN(num) || num < 1) {
      setError('Please enter a valid number (at least 1).');
      return null;
    }
    if (num > 150) {
      setError('Maximum number of questions is 150.');
      return null;
    }

    setError('');
    return num;
  };

  const handleStart = () => {
    const num = validateAndGetNumber();
    if (num === null) return;

    navigate('/tutor', {
      state: {
        group,
        numQuestions: num,
        filter: questionSource === 'all' ? null : questionSource,
      }
    });
  };

  return (
    <Container className="mt-5" style={{ maxWidth: '500px' }}>
      <Card>
        <Card.Body>
          <h2 className="mb-4">Tutor Session Setup</h2>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Number of questions (max 150)</Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={numQuestionsInput}
                isInvalid={!!error}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9]/g, '');
                  setNumQuestionsInput(val);
                  setError('');
                }}
                placeholder="Enter number"
              />
              <Form.Text className="text-muted">
                If fewer questions are available, you'll get as many as possible.
              </Form.Text>
              {error && (
                <Form.Text className="text-danger d-block mt-1">
                  {error}
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Question source</Form.Label>
              {loadingCounts && <Spinner animation="border" size="sm" />}
              {fetchError && <Alert variant="danger">{fetchError}</Alert>}
              {!loadingCounts && !fetchError && (
                <>
                  <Form.Check
                    type="radio"
                    label={`All questions (${counts.all} items)`}
                    name="source"
                    value="all"
                    checked={questionSource === 'all'}
                    onChange={(e) => setQuestionSource(e.target.value)}
                  />
                  <Form.Check
                    type="radio"
                    label={`Only new questions (never answered) (${counts.new} items)`}
                    name="source"
                    value="new"
                    checked={questionSource === 'new'}
                    onChange={(e) => setQuestionSource(e.target.value)}
                  />
                  <Form.Check
                    type="radio"
                    label={`New + previously incorrect (${counts.mixed} items)`}
                    name="source"
                    value="mixed"
                    checked={questionSource === 'mixed'}
                    onChange={(e) => setQuestionSource(e.target.value)}
                  />
                </>
              )}
            </Form.Group>

            <Button variant="primary" onClick={handleStart}>
              Start Tutor Session
            </Button>
            <Button variant="secondary" className="ms-2" onClick={() => navigate(-1)}>
              Cancel
            </Button>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default TutorSetup;